import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  FormHelperText,
  Checkbox,
  RadioGroup,
  Radio,
  FormControlLabel,
  Link
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../config/AppConfig";
import { useNavigate } from "react-router-dom";

const Register = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [mapCenter, setMapCenter] = useState({
    lat: 25.2048493,
    lng: 55.2707828,
  });
  const mapCenterRef = useRef(mapCenter);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    businessName: "",
    address: "",
    businessLocation: "",
    businessType: [],
    category: [],
    phoneCode: "+971",
    altphoneCode : '+971',
    phone: "",
    altPhone: "",
    password: "",
    latitude: "",
    longitude: "",
  });

  const [errors, setErrors] = useState({});
  const [useFreeSubscription, setUseFreeSubscription] = useState("no");
  const [freePlanData, setFreePlanData] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const businessTypes = [
    { id: 1, name: "Importer" },
    { id: 2, name: "Trader" },
    { id: 3, name: "Retailer" },
  ];

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  // Load Google Maps
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (!window.google) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBu_hPjmC3jAI9s0wVqyRc03AWS4p0OLxc&libraries=places`;
        script.async = true;
        document.body.appendChild(script);
        script.onload = () => initMapAndAutocomplete();
      } else {
        initMapAndAutocomplete();
      }
    };

    const initMapAndAutocomplete = () => {
      const initialMap = new window.google.maps.Map(document.getElementById("map"), {
        center: mapCenterRef.current,
        zoom: 12,
      });

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLat = position.coords.latitude;
            const userLng = position.coords.longitude;
            initialMap.setCenter({ lat: userLat, lng: userLng });
            setMapCenter({ lat: userLat, lng: userLng });
          },
          () => {
            console.warn("Geolocation denied, using default");
          }
        );
      }

      const initialMarker = new window.google.maps.Marker({
        position: mapCenterRef.current,
        map: initialMap,
        title: "Selected Location",
      });

      const input = document.getElementById("businessLocation");
      if (!input) return;

      const autocomplete = new window.google.maps.places.Autocomplete(input);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setFormData((prev) => ({
            ...prev,
            businessLocation: place.formatted_address,
            latitude: lat.toString(),
            longitude: lng.toString(),
          }));

          initialMap.setCenter({ lat, lng });
          initialMap.setZoom(12);
          if (initialMarker) {
            initialMarker.setPosition({ lat, lng });
            initialMarker.setTitle(place.formatted_address);
          }
        }
      });
    };

    loadGoogleMaps();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/get_category?user_id=1`);
        setCategories(
          res.data.categories_arr !== "NA" ? res.data.categories_arr : []
        );
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchFreePlan = async () => {
      try {
        const res = await axios.get(
          "https://winkdic.com/2025/B2B/server/get_free_subscription"
        );
        if (res.data.success && res.data.subscription_arr) {
          setFreePlanData(res.data.subscription_arr);
        }
      } catch (err) {
        console.error("Error fetching free subscription:", err);
      }
    };
    fetchFreePlan();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (name, event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.name) tempErrors.name = "Please enter name";
    if (!formData.email) tempErrors.email = "Please enter email";
    else if (!/^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(formData.email))
      tempErrors.email = "Invalid email format";
    if (!formData.businessName)
      tempErrors.businessName = "Please enter business name";
    if (!formData.phone) tempErrors.phone = "Please enter phone number";
    if (!formData.password) tempErrors.password = "Please enter password";
    if (!formData.address) tempErrors.address = "Please enter address";
    if (!formData.businessLocation)
      tempErrors.businessLocation = "Please enter business location";
    if (!formData.businessType.length)
      tempErrors.businessType = "Please select at least one business type";
    if (!formData.category.length)
      tempErrors.category = "Please select at least one category";

    if (!agreeTerms)
      tempErrors.agreeTerms =
        "You must agree to Terms & Conditions and Privacy Policy.";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      owner_name: formData.name,
      email: formData.email,
      business_name: formData.businessName,
      address: formData.address,
      business_location: formData.businessLocation,
      business_type: formData.businessType.join(","),
      category_ids: formData.category.join(","),
      mobile: formData.phoneCode + " " + formData.phone,
      alternate_mobile: formData.altPhone ? formData.altphoneCode + " " + formData.altPhone : "",
      password: formData.password,
      device_type: "web",
      player_id: "web-" + new Date().getTime(),
      phone_code: formData.phoneCode,
      latitude: formData.latitude || mapCenter.lat.toString(),
      longitude: formData.longitude || mapCenter.lng.toString(),
    };

    try {
      const res = await axios.post(`${API_URL}/sign_up_website`, payload);

      if (res.data.success) {
        const userId = res.data.user_id;
        if (useFreeSubscription === "yes" && freePlanData) {
          const subscriptionPayload = {
            user_id: userId,
            plan_name: freePlanData.plan_name,
            type: freePlanData.type,
            days: freePlanData.days,
            amount: freePlanData.amount,
            payment_status: 1,
            payment_type: "Free",
            product_limit: freePlanData.product_limit,
            unlimited_products: freePlanData.unlimited_products,
            image_limit: freePlanData.image_limit,
            unlimited_images: 1,
            search_limit: freePlanData.search_limit,
            unlimited_search_limit: freePlanData.unlimited_search_limit,
          };

          await axios.post(
            `${API_URL}/purchase_subscription`,
            subscriptionPayload
          );
        }

        Swal.fire({
          title: "Registered successfully! Please wait for admin approval",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => navigate(`/login`));
      } else {
        Swal.fire("Error", res.data.msg || "Something went wrong", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 5, mb: 5 }}>
      <Card sx={{ width: "95%", maxWidth: 1000, p: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={3} fontWeight="bold">
            Create an Account
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  error={!!errors.businessName}
                  helperText={errors.businessName}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Phone Code"
                  name="phoneCode"
                  value={formData.phoneCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
               <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Phone Code"
                  name="altphoneCode"
                  value={formData.altphoneCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Alternate Phone"
                  name="altPhone"
                  value={formData.altPhone}
                  onChange={handleChange}
                  error={!!errors.altPhone}
                  helperText={errors.altPhone}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Location"
                  id="businessLocation"
                  name="businessLocation"
                  value={formData.businessLocation}
                  onChange={handleChange}
                  error={!!errors.businessLocation}
                  helperText={errors.businessLocation}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  id="map"
                  sx={{ height: 300, width: "100%", borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.businessType}>
                  <InputLabel>Business Type</InputLabel>
                  <Select
                    multiple
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) => handleMultiSelectChange("businessType", e)}
                    input={<OutlinedInput label="Business Type" />}
                    renderValue={(selected) =>
                      selected
                        .map(
                          (id) =>
                            businessTypes.find((b) => b.id === id)?.name || id
                        )
                        .join(", ")
                    }
                    MenuProps={MenuProps}
                  >
                    {businessTypes.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        <Checkbox
                          checked={formData.businessType.includes(b.id)}
                        />
                        <Typography>{b.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.businessType}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>Dealing With</InputLabel>
                  <Select
                    multiple
                    name="category"
                    value={formData.category}
                    onChange={(e) => handleMultiSelectChange("category", e)}
                    input={<OutlinedInput label="Category" />}
                    renderValue={(selected) =>
                      selected
                        .map(
                          (id) =>
                            categories.find((c) => c.category_id === id)
                              ?.category_name
                        )
                        .join(", ")
                    }
                    MenuProps={MenuProps}
                  >
                    {categories.map((c) => (
                      <MenuItem key={c.category_id} value={c.category_id}>
                        <Checkbox
                          checked={formData.category.includes(c.category_id)}
                        />
                        <Typography>{c.category_name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.category}</FormHelperText>
                </FormControl>
              </Grid>
              {/* Free Subscription Option */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Use Free Subscription
                  </Typography>
                  <RadioGroup
                    row
                    name="useFreeSubscription"
                    value={useFreeSubscription}
                    onChange={(e) => setUseFreeSubscription(e.target.value)}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Show Free Subscription Info if Yes */}
              {useFreeSubscription === "yes" && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Free Subscription Details
                    </Typography>
                    <ul>
                      <li>Unlimited Products</li>
                      <li>Unlimited Searches</li>
                      <li>Valid for {freePlanData?.days} Days</li>
                    </ul>
                    <Box
                      sx={{
                        backgroundColor: "#e6f5ec",
                        padding: "12px",
                        borderRadius: "6px",
                        color: "#2e7d32",
                        fontWeight: 500,
                      }}
                    >
                      ⭐ Perfect for getting started with your business journey!
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* ✅ Terms Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{" "}
                      <Link href="/terms-and-conditions" target="_blank">
                        Terms & Conditions
                      </Link>{" "}
                      and{" "}
                      <Link href="/privacy-policy" target="_blank">
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  }
                />
                {errors.agreeTerms && (
                  <FormHelperText error>{errors.agreeTerms}</FormHelperText>
                )}
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  style={{ background: "#f47b1e" }}
                  fullWidth
                >
                  Register
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
